import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Film, Image as ImageIcon, FileText, Upload, Trash2, Loader2, Download, Clapperboard } from 'lucide-react';

const tipoIcon = (mt) => {
  if ((mt || '').startsWith('video')) return <Film size={20} className="shrink-0 text-violet-500" />;
  if ((mt || '').startsWith('image')) return <ImageIcon size={20} className="shrink-0 text-sky-500" />;
  return <FileText size={20} className="shrink-0 text-indigo-500" />;
};

const tipoLabel = (mt) => {
  if ((mt || '').startsWith('video')) return 'Video';
  if ((mt || '').startsWith('image')) return 'Immagine';
  return 'File';
};

const fmtSize = (b) => {
  if (!b) return '';
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export const MediaRisorse = ({ isAdmin }) => {
  const [risorse, setRisorse] = useState([]);
  const [titolo, setTitolo] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [keywords, setKeywords] = useState('');
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get('/ai-admin/media-risorse');
      setRisorse(r.data.risorse || []);
    } catch { toast.error('Errore caricamento risorse'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!titolo.trim()) { toast.error('Indica un titolo'); return; }
    if (!file) { toast.error('Seleziona un file'); return; }
    if (file.size > 16 * 1024 * 1024) { toast.error('File troppo grande (max 16MB)'); return; }
    const fd = new FormData();
    fd.append('titolo', titolo.trim());
    fd.append('descrizione', descrizione.trim());
    fd.append('keywords', keywords.trim());
    fd.append('caption', caption.trim());
    fd.append('file', file);
    setUploading(true);
    try {
      await api.post('/ai-admin/media-risorse', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      toast.success('Risorsa caricata. Sofia potrà inviarla ai clienti quando pertinente.');
      setTitolo(''); setDescrizione(''); setKeywords(''); setCaption(''); setFile(null);
      const input = document.getElementById('media-file-input');
      if (input) input.value = '';
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento');
    } finally { setUploading(false); }
  };

  const elimina = async (id) => {
    if (!window.confirm('Eliminare questa risorsa? Sofia non potrà più inviarla.')) return;
    try {
      await api.delete(`/ai-admin/media-risorse/${id}`);
      toast.success('Risorsa eliminata'); load();
    } catch { toast.error('Errore eliminazione'); }
  };

  const scarica = async (d) => {
    try {
      const r = await api.get(`/ai-admin/media-risorse/${d.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = d.filename || 'file'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('File non disponibile'); }
  };

  return (
    <div className="space-y-6" data-testid="media-risorse">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clapperboard size={18} className="text-violet-600" /> Risorse / Media di Sofia
          </CardTitle>
          <p className="text-sm text-slate-500">
            Carica video tutorial, immagini o file (es. "Come accedere all'area clienti UnipolSai"). Quando un cliente
            fa una domanda pertinente su WhatsApp, Sofia invierà automaticamente la risorsa giusta.
            Indica <b>Titolo</b>, <b>Descrizione</b> e <b>parole chiave</b> così Sofia capisce quando usarla.
            Max 16MB per file (consigliati video brevi).
          </p>
        </CardHeader>
        {isAdmin && (
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-slate-500">Titolo *</Label>
                <Input
                  placeholder="Es. Accesso area clienti UnipolSai"
                  value={titolo}
                  onChange={(e) => setTitolo(e.target.value)}
                  data-testid="media-titolo-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Parole chiave (separate da virgola)</Label>
                <Input
                  placeholder="area clienti, unipolsai, accesso, login"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  data-testid="media-keywords-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Descrizione (quando Sofia deve inviarlo)</Label>
              <Textarea
                placeholder="Es. Video che spiega passo passo come accedere all'area riservata di UnipolSai dal sito"
                value={descrizione}
                onChange={(e) => setDescrizione(e.target.value)}
                rows={2}
                data-testid="media-descrizione-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Didascalia inviata col file (opzionale)</Label>
              <Input
                placeholder="Es. Ecco il video che spiega come accedere 👇"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                data-testid="media-caption-input"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <Input
                id="media-file-input"
                type="file"
                accept=".mp4,.mov,.3gp,.avi,.mkv,.webm,.jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="media-file-input"
                className="cursor-pointer"
              />
              <Button onClick={upload} disabled={uploading} data-testid="media-upload-btn" className="w-full sm:w-auto">
                {uploading ? <><Loader2 size={15} className="mr-2 animate-spin" /> Caricamento...</> : <><Upload size={15} className="mr-2" /> Carica risorsa</>}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      <div className="space-y-2">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : risorse.length === 0 ? (
          <p className="text-center text-sm text-slate-400 py-8">Nessuna risorsa caricata. Carica il primo video o file.</p>
        ) : (
          risorse.map((d) => (
            <div key={d.id} className="flex items-center gap-3 rounded-lg border bg-white p-3" data-testid={`media-item-${d.id}`}>
              {tipoIcon(d.mimetype)}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-800">{d.titolo}</span>
                  <Badge className="bg-violet-100 text-violet-700">{tipoLabel(d.mimetype)}</Badge>
                  {d.size ? <span className="text-xs text-slate-400">{fmtSize(d.size)}</span> : null}
                </div>
                {d.descrizione ? <p className="truncate text-xs text-slate-500">{d.descrizione}</p> : null}
                {d.keywords ? <p className="truncate text-[11px] text-slate-400">🔑 {d.keywords}</p> : null}
              </div>
              <Button variant="ghost" size="sm" onClick={() => scarica(d)} data-testid={`media-download-${d.id}`}>
                <Download size={15} />
              </Button>
              {isAdmin && (
                <Button variant="ghost" size="sm" onClick={() => elimina(d.id)} data-testid={`media-delete-${d.id}`} className="text-red-500 hover:text-red-600">
                  <Trash2 size={15} />
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
